<div v-if="is_pdf_output_format">
  <formal-document-template-wizard
    :key="pdf_builder_render_key"
    :allow_one_off="Boolean(initial_target_entity_profile_id)"
    :formal_lookups="formal_lookup_data"
    :initial_application_id="initial_application_id"
    :initial_entity_name="initial_entity_name"
    :initial_entity_type_id="initial_entity_type_id"
    :initial_template="pdf_builder_template"
    :initial_target_entity_profile_id="initial_target_entity_profile_id"
    :report_lookups="lookup_data"
    :selected_output_format_id="selected_output_format_id"
    @cancel="handle_pdf_builder_cancel"
    @change-output-format="handle_pdf_builder_output_change"
    @generated="$emit('generated', $event)"
    @saved="handle_pdf_template_saved"
  ></formal-document-template-wizard>
</div>

<div v-else class="d-grid gap-4 report-builder-shell">
  <div class="card border rounded-lg shadow-sm bg-white report-builder-header-card">
    <div class="d-flex flex-column flex-xl-row justify-content-between align-items-xl-start gap-3 mb-4">
      <div>
        <p class="text-uppercase text-muted text-xxs mb-1">{{ translate_label("Report Builder") }}</p>
        <h3 class="text-x fw-bold mb-1">{{ builder_title }}</h3>
        <p class="text-muted text-xxs mb-0">
          {{ builder_description }}
        </p>
      </div>
      <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-3 py-2">
        {{ selected_report_type_label }}
      </span>
    </div>

    <div v-if="is_editing_template" class="card border rounded-lg p-3 shadow-sm bg-light mb-4">
      <p class="fw-bold text-dark text-xxs mb-1">{{ translate_label("Editing saved template") }}</p>
      <p class="text-muted text-xxs mb-0">
        {{ translate_label("This builder is preloaded from a saved template. Generate when you are happy with the updated configuration.") }}
      </p>
    </div>

    <div class="card border rounded-lg p-3 shadow-sm bg-light report-builder-progress-card">
      <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div>
          <p class="text-uppercase text-muted text-xxs mb-1">{{ step_progress_label }}</p>
          <h4 class="text-x fw-bold mb-1">{{ current_step.title }}</h4>
          <p class="text-muted text-xxs mb-0">
            {{ translate_label("Move through one step at a time for a simpler, mobile-friendly setup experience.") }}
          </p>
        </div>
      </div>

      <div class="d-flex gap-2 overflow-auto report-builder-steps mt-3">
      <button
        v-for="(step, index) in builder_steps"
        :key="step.key"
        class="btn text-start shadow-sm report-step-pill report-step-pill-compact"
        :class="{
          'report-step-pill-active': get_step_status(step.key) === 'active',
          'report-step-pill-complete': get_step_status(step.key) === 'complete',
          'report-step-pill-upcoming': get_step_status(step.key) === 'upcoming',
        }"
        :disabled="index > current_step_index"
        type="button"
        @click="open_step(step.key)"
      >
        <div class="d-flex align-items-center gap-2">
          <span class="report-step-index-badge">
            {{ index + 1 }}
          </span>
          <div>
            <p class="fw-bold text-dark text-xxs mb-0">{{ step.title }}</p>
            <p class="text-muted text-xxs mb-0">{{ step.summary }}</p>
          </div>
        </div>
      </button>
      </div>
    </div>
  </div>

  <div class="card border rounded-lg shadow-sm bg-white report-step-card report-wizard-step-card">
    <div class="px-4 pt-4 pb-2">
      <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div>
          <p class="text-uppercase text-muted text-xxs mb-1">{{ step_progress_label }}</p>
          <h4 class="text-x fw-bold mb-1">{{ current_step.title }}</h4>
          <p class="text-muted text-xxs mb-0">{{ current_step.summary }}</p>
        </div>
        <span class="badge rounded-small text-xxs px-3 py-2 shadow-sm bg-light text-dark">
          {{
            is_review_step
              ? translate_label("Final step")
              : translate_label("Next up")
          }}
        </span>
      </div>
    </div>
    <div class="px-4 pb-4">
      <div v-if="current_step.key === 'basics'" class="d-grid gap-4">
        <div class="card border rounded-lg p-3 shadow-sm bg-light">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div>
              <p class="fw-bold text-dark text-xxs mb-1">{{ translate_label("Save as reusable template") }}</p>
              <p class="text-muted text-xxs mb-0">
                {{ translate_label("Keep this off for fast one-off runs. Turn it on only when the setup should appear in saved templates.") }}
              </p>
            </div>
            <div class="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input
                v-model="save_as_template"
                :disabled="is_editing_template"
                class="form-check-input shadow-sm"
                type="checkbox"
              />
              <label class="form-check-label text-xxs fw-semibold">
                {{ save_as_template ? translate_label("Template will be saved") : translate_label("One-off report") }}
              </label>
            </div>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-12 col-xl-4">
            <label class="modal-form-label form-label text-xxs">{{ translate_label("Report type") }}</label>
            <select
              v-model="selected_report_type_id"
              class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
            >
              <option disabled value="">Select report type</option>
              <option v-for="report_type in report_types" :key="report_type.id" :value="report_type.id">
                {{ report_type.display_name }}
              </option>
            </select>
          </div>
          <div class="col-12 col-xl-4">
            <label class="modal-form-label form-label text-xxs">{{ translate_label("Output format") }}</label>
            <select
              v-model="selected_output_format_id"
              class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
            >
              <option disabled value="">Select output format</option>
              <option v-for="format in output_formats" :key="format.id" :value="format.id">
                {{ format.display_name }}
              </option>
            </select>
          </div>
          <div class="col-12 col-xl-4">
            <label class="modal-form-label form-label text-xxs">{{ translate_label("Template name") }}</label>
            <input
              v-model.trim="template_name"
              class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
              :disabled="!save_as_template"
              :placeholder="selected_report_type_label"
              type="text"
            />
            <p v-if="!save_as_template" class="text-muted text-xxs mt-2 mb-0">
              {{ translate_label("A temporary file name is generated automatically for one-off runs.") }}
            </p>
          </div>
          <div class="col-12">
            <label class="modal-form-label form-label text-xxs">{{ translate_label("Description") }}</label>
            <input
              v-model.trim="template_description"
              class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
              :disabled="!save_as_template"
              :placeholder="translate_label('Optional description')"
              type="text"
            />
          </div>
        </div>
      </div>

      <div v-else-if="current_step.key === 'scope'" class="d-grid gap-4">
        <div class="card border rounded-lg p-4 shadow-sm bg-white">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
            <div>
              <h5 class="text-x fw-bold mb-1">{{ translate_label("Entity scope") }}</h5>
              <p class="text-muted text-xxs mb-0">
                {{ translate_label("Start broad, then narrow the report only when you need a more focused result set.") }}
              </p>
            </div>
            <div v-if="!is_profile_scope" class="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input v-model="use_project_scope" class="form-check-input shadow-sm" type="checkbox" />
              <label class="form-check-label text-xxs fw-semibold">{{ translate_label("Use all entity types") }}</label>
            </div>
          </div>

          <div v-if="is_profile_scope" class="card border rounded-lg p-3 shadow-sm bg-light">
            <p class="fw-bold text-dark text-xxs mb-1">{{ initial_entity_name || translate_label("Selected entity") }}</p>
            <p class="text-muted text-xxs mb-0">
              {{ translate_label("This report will target the selected profile only, while question choices stay aligned to its entity type.") }}
            </p>
          </div>

          <div v-else-if="!use_project_scope" class="row g-3">
            <div v-for="entity_type in entity_types" :key="entity_type.id" class="col-12 col-md-6 col-xl-4">
              <label
                class="card border rounded-lg p-3 shadow-sm h-100"
                :class="{ 'border-primary': selected_entity_type_ids.includes(entity_type.id) }"
              >
                <div class="d-flex align-items-center gap-2">
                  <input
                    :checked="selected_entity_type_ids.includes(entity_type.id)"
                    class="form-check-input shadow-sm"
                    type="checkbox"
                    @change="toggle_entity_type(entity_type.id)"
                  />
                  <span class="fw-semibold text-dark text-xxs">{{ entity_type.entity_type_name }}</span>
                </div>
              </label>
            </div>
          </div>

          <p v-else class="text-muted text-xxs mb-0">
            {{ translate_label("The report will include every entity type assigned to this project.") }}
          </p>
        </div>

        <div class="card border rounded-lg p-4 shadow-sm bg-white">
          <div class="mb-4">
            <h5 class="text-x fw-bold mb-1">{{ translate_label("Date range") }}</h5>
            <p class="text-muted text-xxs mb-0">{{ date_dimension_help_text }}</p>
          </div>

          <div class="row g-3">
            <div class="col-12 col-xl-4">
              <label class="modal-form-label form-label text-xxs">{{ translate_label("Date dimension") }}</label>
              <select
                v-model="selected_date_dimension_id"
                class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
              >
                <option value="">No date filter</option>
                <option
                  v-for="date_dimension in allowed_date_dimensions"
                  :key="date_dimension.id"
                  :value="date_dimension.id"
                >
                  {{ date_dimension.display_name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-xl-4">
              <label class="modal-form-label form-label text-xxs">{{ translate_label("Date from") }}</label>
              <input
                v-model="selected_date_from"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                type="date"
              />
            </div>
            <div class="col-12 col-xl-4">
              <label class="modal-form-label form-label text-xxs">{{ translate_label("Date to") }}</label>
              <input
                v-model="selected_date_to"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
                type="date"
              />
            </div>
          </div>
        </div>
      </div>

      <report-builder-data-selection-step
        v-else-if="current_step.key === 'data_selection'"
        :available_forms="available_forms"
        :empty_selected_questions_description="empty_selected_questions_description"
        :empty_selected_questions_title="empty_selected_questions_title"
        :filtered_questions="filtered_questions"
        :format_question_type="format_question_type"
        :get_allowed_metric_types="get_allowed_metric_types"
        :is_question_scope_report="is_question_scope_report"
        :is_question_selected="is_question_selected"
        :is_question_statistics_summary="is_question_statistics_summary"
        :is_select_type="is_select_type"
        :loading_forms="loading_forms"
        :loading_main_sections="loading_main_sections"
        :loading_questions="loading_questions"
        :loading_sub_sections="loading_sub_sections"
        :main_sections="main_sections"
        :non_question_scope_summary_description="non_question_scope_summary_description"
        :question_search="question_search"
        :selected_form_id="selected_form_id"
        :selected_main_section_id="selected_main_section_id"
        :selected_questions="selected_questions"
        :selected_questions_description="selected_questions_description"
        :selected_sub_section_id="selected_sub_section_id"
        :sub_sections="sub_sections"
        :translate_label="translate_label"
        @remove-question="remove_question"
        @toggle-question-metric="toggle_question_metric($event.question_id, $event.metric_type_id)"
        @toggle-question-selection="toggle_question_selection"
        @update:question_search="handle_question_search_update"
        @update:selected_form_id="handle_form_selection"
        @update:selected_main_section_id="handle_main_section_selection"
        @update:selected_sub_section_id="handle_sub_section_selection"
      ></report-builder-data-selection-step>

      <report-builder-columns-step
        v-else-if="current_step.key === 'columns'"
        :available_report_fields="available_report_fields"
        :get_field_display_label="get_field_display_label"
        :has_available_report_fields="has_available_report_fields"
        :missing_report_fields_message="missing_report_fields_message"
        :preview_columns="preview_columns"
        :selected_field_ids="selected_field_ids"
        :translate_label="translate_label"
        @toggle-field="toggle_field"
        @update-field-display-label="handle_field_display_label_change"
      ></report-builder-columns-step>

      <report-builder-review-step
        v-else-if="current_step.key === 'review'"
        :builder_summary_cards="builder_summary_cards"
        :generated_report="generated_report"
        :get_preview_column_label="get_preview_column_label"
        :layout_preview_text="layout_preview_text"
        :preview_columns="preview_columns"
        :save_as_template="save_as_template"
        :translate_label="translate_label"
      ></report-builder-review-step>
    </div>
  </div>

  <div class="card border rounded-lg shadow-sm bg-white report-builder-footer">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 p-3">
      <div>
        <p class="text-uppercase text-muted text-xxs mb-1">{{ footer_title }}</p>
        <p class="fw-bold text-dark text-xxs mb-1">{{ footer_value }}</p>
        <p class="text-muted text-xxs mb-0">{{ footer_description }}</p>
      </div>

      <div class="d-flex flex-column flex-sm-row gap-2 justify-content-end">
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          :disabled="!previous_step_key"
          type="button"
          @click="go_to_previous_step"
        >
          {{ translate_label("Back") }}
        </button>
        <button
          v-if="!is_review_step"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="go_to_next_step"
        >
          {{ translate_label("Next") }}
        </button>
        <button
          v-if="is_review_step"
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          :disabled="loading_lookups || loading_template || generating_report"
          type="button"
          @click="submit_report_generation"
        >
          <span v-if="generating_report" class="spinner-border spinner-border-sm me-2"></span>
          {{ review_generate_label }}
        </button>
      </div>
    </div>
  </div>
</div>
